// Файл: App.js
// Автор: [Ваше Имя]
// Дата создания: 25 марта 2025
// Описание: Основной компонент CRM-системы для риелторов с интеграцией ROBOKASSA
import { useEffect, useMemo, useState } from 'react'
import './App.css'
import './index.css'
import { Outlet, useLocation } from 'react-router-dom'
import { AppProvider } from '@toolpad/core/react-router-dom'

import { SlSpeedometer } from 'react-icons/sl'
import { TfiViewListAlt } from 'react-icons/tfi'
import { BsHouses } from 'react-icons/bs'
import { TbUsers } from 'react-icons/tb'
import { RiUserHeartLine } from 'react-icons/ri'
import { GoGear } from 'react-icons/go'

// Удаляем импорт логотипа, так как используем файл из папки public

import { injectStores } from '@mobx-devtools/tools'
import objectStore from './stores/objectStore'
import helperStore from './stores/helperStore'
import filterStore from './stores/filterStore'
import i18nStore from './stores/i18nStore'
import requestStore from './stores/requestStore'
import sessionStore from './stores/sessionStore'
import { observer } from 'mobx-react-lite'
import { CircularProgress } from '@mui/material'
import reldoTheme from './theme/theme'
import statusStore from './stores/statusStore'
import organizationSessionStore from './stores/organizationSessionStore'
import organizationManagementStore from './stores/organizationManagementStore'
/* import debugStore from './stores/debugStore' */

import logger from './utils/logger'
import { TfiHelpAlt } from "react-icons/tfi";
import { HiMiniCodeBracket } from "react-icons/hi2";
import { MdOutlineFileUpload } from "react-icons/md";
/* import { fixStaticResourcePath } from './utils/imageUtils' */

const GUEST_ONLY_PATHS = new Set([
	'/login',
	'/register',
	'/forgot',
	'/verify-email',
	'/verify-email-pending',
	'/reset-password',
])


// Инъекция хранилищ только в режиме разработки
if (import.meta.env.VITE_APP_MODE === 'development') {
	injectStores({
		objectStore,
		helperStore,
		filterStore,
		i18nStore,
		requestStore,
		sessionStore,
		statusStore,
		organizationSessionStore,
		/* debugStore, */
	})
	console.log('MobX DevTools: хранилища подключены в режиме разработки')
}

const App = observer(() => {
	const location = useLocation()
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [authCheckComplete, setAuthCheckComplete] = useState(false)
	const authenticatedUserId = sessionStore.user?.id
	const organizationGeneration = organizationSessionStore.contextGeneration
	const organizationStatus = organizationSessionStore.status
	const selectedOrganizationId = organizationSessionStore.selectedOrganizationId

	useEffect(() => {
		if (GUEST_ONLY_PATHS.has(location.pathname)) {
			sessionStore.setUnauthenticated()
			setAuthCheckComplete(true)
			return
		}

		const checkAuth = async () => {
			try {
				await sessionStore.bootstrap()
			} catch (error) {
				logger.error('Ошибка проверки авторизации', { name: error.name })
			} finally {
				setAuthCheckComplete(true)
			}
		}
		checkAuth()
	}, [location.pathname])

	// Шаг 2: Инициализация приложения только после проверки авторизации
	useEffect(() => {
		// Не начинаем загрузку данных, пока не проверим авторизацию
		if (!authCheckComplete) return;

/* 		debugStore.showComponentName = true
		debugStore.showGetMeButton = true */

		const initializeApp = async () => {
			setIsLoading(true)
			try {
				// Public static translations are loaded for guests. Language and country
				// catalogs are needed only in an authenticated user settings context.
				await i18nStore.initializeLanguage(undefined, {
					loadCatalogs: Boolean(sessionStore.user),
				})
				
				// Если пользователь не авторизован, просто завершаем инициализацию
				if (!sessionStore.user) {
					logger.log('👨‍🦱 Пользователь не авторизован, перенаправляем на страницу входа');
					setIsInitialized(true);
					return;
				}

				if (!organizationSessionStore.isReady) {
					logger.warn('Organization context is not ready', {
						status: organizationSessionStore.status,
						errorCode: organizationSessionStore.errorCode,
					})
					setIsInitialized(true)
					return
				}
				await organizationManagementStore.refreshIncomingInvitations()
				
				logger.log('Пользователь авторизован, инициализация CRM')
				
				// Загружаем базовые компоненты сначала
				await objectStore.fetchCategoriesAndFiles()
				
				// Затем загружаем компоненты, требующие авторизации
				await Promise.all([
					statusStore.fetchStatuses(),
					statusStore.fetchTags('App.jsx')
				]);
				
				// В последнюю очередь загружаем коллег
				setIsInitialized(true)
			} catch (error) {
				logger.error('Ошибка инициализации приложения:', error)
			} finally {
				setIsLoading(false)
			}
		}

		initializeApp()
	}, [
		authCheckComplete,
		authenticatedUserId,
		organizationGeneration,
		organizationStatus,
		selectedOrganizationId,
	]);

	const session = sessionStore.user ? {
		user: { email: sessionStore.user.email, image: '/assets/default-avatar.png' },
	} : null

	const authentication = useMemo(() => {
		return {
			signIn: async () => sessionStore.bootstrap(),
			signOut: () => {
				sessionStore.logout()
			},
		}
	}, [])

	// Отображаем "Загрузка..." до инициализации приложения
	if (!authCheckComplete || isLoading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
				<div style={{ marginLeft: '10px' }}>
					{!authCheckComplete ? 'Проверка авторизации...' : 'Загрузка приложения...'}
				</div>
			</div>
		)
	}

	if (sessionStore.user && ['bootstrap', 'switching', 'refreshing'].includes(organizationSessionStore.status)) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
				<div style={{ marginLeft: '10px' }}>Переключение организации...</div>
			</div>
		)
	}

	// Проверка инициализации только для авторизованных пользователей
	if (sessionStore.user && !isInitialized) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
				<div style={{ marginLeft: '10px' }}>
					Загрузка данных...
				</div>
			</div>
		)
	}

	if (sessionStore.user && !organizationSessionStore.isReady) {
		return (
			<div role='alert' style={{ padding: '24px' }}>
				Контекст организации недоступен. Повторите вход или обновите страницу.
			</div>
		)
	}

	const NAVIGATION = [
		{
			title: i18nStore.translations.menu.dashboard || 'Главная',
			icon: <SlSpeedometer size={20} />,
		},
		{
			segment: 'requests',
			title: i18nStore.translations.menu.requests || 'Заявки',
			icon: <TfiViewListAlt size={20} />,
		},
		{
			segment: `objects`,
			title: i18nStore.translations.menu.properties || 'Объекты',
			icon: <BsHouses size={20} />,
		},
		{
			segment: `customers`,
			title: i18nStore.translations.menu.clients || 'Клиенты',
			icon: <TbUsers size={20} />,
		},
		{
			segment: `members`,
			title: 'Профили и поиск',
			icon: <RiUserHeartLine size={20} />,
		},
		{
			segment: `parser`,
			title: i18nStore.translations.menu.parser || 'Парсер',
			icon: <HiMiniCodeBracket size={20} />,
		},
		{
			segment: 'avito',
			title: 'Авито',
			icon: <MdOutlineFileUpload size={20} />,
		},
		{
			segment: 'options',
			title: i18nStore.translations.menu.settings || 'Настройки',
			icon: <GoGear size={20} />,
		},
		{
			segment: 'help',
			title: i18nStore.translations.menu.help || 'Помощь',
			icon: <TfiHelpAlt size={20} />,
		},
	
	]

	return (
		<AppProvider
			theme={reldoTheme}
			navigation={NAVIGATION}
			session={session}
			authentication={authentication}
			branding={{
				logo: <img src="/logo.png" alt='Reldo' />,
				title: 'Reldo',
			}}
		>
			<Outlet key={organizationSessionStore.contextGeneration} />
		</AppProvider>
	)
})

export default App
